import React, { useMemo, useEffect } from 'react'
import { Menu, Typography, Box } from '@mui/material'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { useLocation, useNavigate } from 'react-router-dom'
import { LinkButton } from '@e3dc-react/shell/Elements/Buttons'
import { makeStyles } from 'tss-react/mui'
import { J2Checking } from '@e3dc-react/icons'
import NotificationMenuItem from './NotificationMenuItem'
import useNotificationContext from '../../hooks/use-notification-context'
import useModals from 'src/Hooks/useModals'

const useStyles = makeStyles()(theme => ({
  menu: {
    minWidth: 315,
    maxWidth: 445,
    [theme.breakpoints.down('md')]: {
      minWidth: 315,
      maxWidth: 315,
    },
  },
  list: {
    backgroundColor: theme.palette.background.header,
  },
  linkButton: {
    textDecoration: 'none',
    color: theme.palette.primary.main,
    textTransform: 'uppercase',
    '&:hover': {
      cursor: 'pointer',
    },
  },
}))

const UNREAD_NOTIFICATION_LIMIT = 5

interface Props {
  notificationAnchor: HTMLButtonElement | null
  onClose: () => void
}

/**
 * Displays a preview of unread notifications and allows user to re-route to notification page
 */
const NotificationMenu: React.VFC<Props> = ({ notificationAnchor, onClose }) => {
  const { t } = useCustomTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { classes } = useStyles()

  const { openNotificationMessageModal } = useModals()
  const { unreadNotifications, markNotificationAsRead, loadNotifications, markAllNotificationsAsRead, unreadNotificationsCount } =
    useNotificationContext()

  /**
   * display surveys at the top of the list, leave the order of the rest as is
   */
  const sortedUnreadNotifications = useMemo(() => {
    const surveys = unreadNotifications.filter(notification => notification.type === 'survey')
    const otherNotifications = unreadNotifications.filter(notification => notification.type !== 'survey')
    return [...surveys, ...otherNotifications].slice(0, UNREAD_NOTIFICATION_LIMIT)
  }, [unreadNotifications])

  const isOnNotificationPage = location.pathname === '/notifications'
  const hasNoMessages = unreadNotifications.length === 0
  const availableMessagesCount = unreadNotificationsCount - UNREAD_NOTIFICATION_LIMIT

  const onClickViewNotificationItem = (notificationId: number): void => {
    const notificationMessage = unreadNotifications.find(notification => notification.id === notificationId)
    if (notificationMessage) openNotificationMessageModal({ notificationMessage, markNotificationAsRead })
  }

  const onClickViewAllNotifications = (): void => {
    navigate('/notifications')
    onClose()
  }

  useEffect(() => {
    if (notificationAnchor) {
      loadNotifications()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notificationAnchor])

  return (
    <Menu
      id="notifications-menu"
      anchorEl={notificationAnchor}
      open={!!notificationAnchor}
      onClose={onClose}
      disableScrollLock={true}
      classes={{ paper: classes.menu, list: classes.list }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <Box sx={{ p: 4 }}>
        <Typography variant="h6" noWrap>
          {t('notifications.title')}
        </Typography>
        {sortedUnreadNotifications.map(notification => (
          <NotificationMenuItem key={`notification - ${notification.id}`} notification={notification} onClick={onClickViewNotificationItem} />
        ))}
        {unreadNotificationsCount > UNREAD_NOTIFICATION_LIMIT && (
          <Box display="flex" justifyContent="center">
            <Typography variant={'body2'} sx={{ pt: 2 }}>
              {`${availableMessagesCount} ${t('notifications.more')}`}
            </Typography>
          </Box>
        )}
        {hasNoMessages && (
          <Typography variant={'body2'} sx={{ pt: 2 }}>
            {t('notification.headerMenu.empty')}
          </Typography>
        )}
        {!isOnNotificationPage && !hasNoMessages && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <LinkButton
              linkClassName={classes.linkButton}
              onClick={markAllNotificationsAsRead}
              content={
                <Box display="flex" alignItems="center">
                  <J2Checking />
                  <Typography variant="caption" ml={1} sx={{ fontSize: '11px !important' }}>
                    {t('notifications.read-all')}
                  </Typography>
                </Box>
              }
            />
          </Box>
        )}
        {!isOnNotificationPage && (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <LinkButton
              linkClassName={classes.linkButton}
              content={
                <Typography variant="caption" sx={{ fontSize: '11px !important' }}>
                  {t('notification.headerMenu.seeMore')}
                </Typography>
              }
              onClick={onClickViewAllNotifications}
              data-qa="view-all-notifications-button"
            />
          </Box>
        )}
      </Box>
    </Menu>
  )
}

export default NotificationMenu
